import apiService from '../api'

// ────────────────────────── Shared Types ──────────────────────────

export interface PaginationMeta {
  page: number
  limit: number
  total: number
  totalPages: number
}

export interface ApiResponse<T> {
  success: boolean
  data: T
  message?: string
  meta?: PaginationMeta
}

export interface ReportQueryParams {
  page?: number
  limit?: number
  startDate?: string
  endDate?: string
  status?: string
}

// ────────────────────────── Contract Report ──────────────────────────

export interface ContractReportItem {
  id: string
  contractNumber: string
  title: string
  type: 'buy' | 'sale'
  price?: string | null
  quantity?: string | null
  sku?: string | null
  startDate?: string | null
  endDate?: string | null
  createdAt: string
  vendor?: { id: string; name: string } | null
}

export interface ContractReportSummary {
  totalContracts: number
  totalValue: number
  buyContracts: number
  saleContracts: number
}

export interface ContractReportResponse {
  contracts: ContractReportItem[]
  summary: ContractReportSummary
}

// ────────────────────────── Shipment Report ──────────────────────────

export interface ShipmentReportItem {
  id: string
  buyContract?: string | null
  saleContract?: string | null
  containerNumber?: string | null
  pol?: string | null
  pod?: string | null
  eta?: string | null
  etd?: string | null
  status?: string | null
  product?: string | null
  customer?: string | null
  carrier?: string | null
  vessel?: string | null
  createdAt: string
}

export interface ShipmentReportSummary {
  totalShipments: number
  byStatus: Record<string, number>
}

export interface ShipmentReportResponse {
  shipments: ShipmentReportItem[]
  summary: ShipmentReportSummary
}

// ────────────────────────── Inventory Report (Stock Recon) ──────────────────────────

export interface InventoryReportItem {
  id: string
  product?: string | null
  warehouse?: string | null
  location?: string | null
  batchNumber?: string | null
  containerNumber?: string | null
  pieces?: number | null
  weight?: number | null
  daysInWarehouse?: number | null
  dispatchDate?: string | null
  dispatchNumber?: string | null
  damageSeverity?: string | null
  createdAt: string
}

export interface InventoryReportSummary {
  totalRows: number
  totalWeight: number
  totalPieces: number
  inWarehouse: number
  dispatched: number
  warehouseCount: number
}

export interface InventoryReportResponse {
  rows: InventoryReportItem[]
  summary: InventoryReportSummary
}

// ────────────────────────── Stock / Dispatch Report ──────────────────────────

export interface StockReportItem {
  id: string
  product?: string | null
  customer?: string | null
  destination?: string | null
  qtyDispatched?: number | null
  qtyUnit?: string | null
  status: string
  dispatchDate?: string | null
  createdAt: string
  warehouse?: { id: string; name: string } | null
}

export interface StockReportSummary {
  totalDispatches: number
  totalQtyDispatched: number
  byStatus: Record<string, number>
}

export interface StockReportResponse {
  dispatches: StockReportItem[]
  summary: StockReportSummary
}

// ────────────────────────── Payment Report ──────────────────────────

export interface PaymentReportItem {
  id: string
  invoice?: string | null
  lotNumber?: string | null
  amount?: number | null
  paidAmount?: number | null
  status: string
  dueDate?: string | null
  paidDate?: string | null
  paymentDate?: string | null
  createdAt: string
  vendor?: { id: string; name: string } | null
  shipment?: { id: string; containerNumber?: string | null; customer?: string | null } | null
}

export interface PaymentReportSummary {
  totalPayments: number
  totalAmount: number
  totalPaid: number
  totalDue: number
  byStatus: Record<string, number>
}

export interface PaymentReportResponse {
  payments: PaymentReportItem[]
  summary: PaymentReportSummary
}

// ────────────────────────── Supplier Report ──────────────────────────

export interface SupplierReportItem {
  supplierName: string
  product: string
  total: number
  delivered: number
  inTransit: number
  pending: number
  status: string
}

export interface SupplierReportSummary {
  totalSuppliers: number
  totalShipments: number
}

export interface SupplierReportResponse {
  suppliers: SupplierReportItem[]
  summary: SupplierReportSummary
}

// ────────────────────────── Client Report ──────────────────────────

export interface ClientReportItem {
  customerName: string
  product: string
  total: number
  delivered: number
  inTransit: number
  pending: number
  status: string
}

export interface ClientReportSummary {
  totalClients: number
  totalShipments: number
}

export interface ClientReportResponse {
  clients: ClientReportItem[]
  summary: ClientReportSummary
}

// ────────────────────────── Product Report ──────────────────────────

export interface ProductReportItem {
  productName: string
  total: number
  delivered: number
  inTransit: number
  pending: number
  status: string
}

export interface ProductReportSummary {
  totalProducts: number
  totalShipments: number
}

export interface ProductReportResponse {
  products: ProductReportItem[]
  summary: ProductReportSummary
}

// ────────────────────────── API Functions ──────────────────────────

function buildQueryString(params: Record<string, any>): string {
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '' && value !== 'Status') {
      qs.set(key, String(value))
    }
  }
  const str = qs.toString()
  return str ? `?${str}` : ''
}

// --- Contracts ---
export async function getContractReport(
  params: ReportQueryParams & { customer?: string; type?: string }
): Promise<{ data: ContractReportResponse; meta: PaginationMeta }> {
  const qs = buildQueryString(params)
  const res = await apiService.get<ApiResponse<ContractReportResponse>>(`/reports/contracts${qs}`)
  return {
    data: res.data ?? { contracts: [], summary: { totalContracts: 0, totalValue: 0, buyContracts: 0, saleContracts: 0 } },
    meta: res.meta ?? { page: 1, limit: 10, total: 0, totalPages: 0 },
  }
}

// --- Shipments ---
export async function getShipmentReport(
  params: ReportQueryParams & { origin?: string; destination?: string }
): Promise<{ data: ShipmentReportResponse; meta: PaginationMeta }> {
  const qs = buildQueryString(params)
  const res = await apiService.get<ApiResponse<ShipmentReportResponse>>(`/reports/shipments${qs}`)
  return {
    data: res.data ?? { shipments: [], summary: { totalShipments: 0, byStatus: {} } },
    meta: res.meta ?? { page: 1, limit: 10, total: 0, totalPages: 0 },
  }
}

// --- Inventory ---
export async function getInventoryReport(
  params: ReportQueryParams & { warehouseId?: string }
): Promise<{ data: InventoryReportResponse; meta: PaginationMeta }> {
  const qs = buildQueryString(params)
  const res = await apiService.get<ApiResponse<InventoryReportResponse>>(`/reports/inventory${qs}`)
  return {
    data: res.data ?? { lots: [], summary: { totalLots: 0, totalTons: 0, byStockLevel: {} } },
    meta: res.meta ?? { page: 1, limit: 10, total: 0, totalPages: 0 },
  }
}

// --- Stock / Dispatches ---
export async function getStockReport(
  params: ReportQueryParams & { warehouseId?: string }
): Promise<{ data: StockReportResponse; meta: PaginationMeta }> {
  const qs = buildQueryString(params)
  const res = await apiService.get<ApiResponse<StockReportResponse>>(`/reports/stock${qs}`)
  return {
    data: res.data ?? { dispatches: [], summary: { totalDispatches: 0, totalQtyDispatched: 0, byStatus: {} } },
    meta: res.meta ?? { page: 1, limit: 10, total: 0, totalPages: 0 },
  }
}

// --- Payments ---
export async function getPaymentReport(
  params: ReportQueryParams & { vendorId?: string }
): Promise<{ data: PaymentReportResponse; meta: PaginationMeta }> {
  const qs = buildQueryString(params)
  const res = await apiService.get<ApiResponse<PaymentReportResponse>>(`/reports/payments${qs}`)
  return {
    data: res.data ?? { payments: [], summary: { totalPayments: 0, totalAmount: 0, totalPaid: 0, totalDue: 0, byStatus: {} } },
    meta: res.meta ?? { page: 1, limit: 10, total: 0, totalPages: 0 },
  }
}

// --- Supplier Reports ---
export async function getSupplierReport(
  params: ReportQueryParams
): Promise<{ data: SupplierReportResponse; meta: PaginationMeta }> {
  const qs = buildQueryString(params)
  const res = await apiService.get<ApiResponse<SupplierReportResponse>>(`/reports/suppliers${qs}`)
  return {
    data: res.data ?? { suppliers: [], summary: { totalSuppliers: 0, totalShipments: 0 } },
    meta: res.meta ?? { page: 1, limit: 10, total: 0, totalPages: 0 },
  }
}

// --- Client Reports ---
export async function getClientReport(
  params: ReportQueryParams
): Promise<{ data: ClientReportResponse; meta: PaginationMeta }> {
  const qs = buildQueryString(params)
  const res = await apiService.get<ApiResponse<ClientReportResponse>>(`/reports/clients${qs}`)
  return {
    data: res.data ?? { clients: [], summary: { totalClients: 0, totalShipments: 0 } },
    meta: res.meta ?? { page: 1, limit: 10, total: 0, totalPages: 0 },
  }
}

// --- Product Reports ---
export async function getProductReport(
  params: ReportQueryParams
): Promise<{ data: ProductReportResponse; meta: PaginationMeta }> {
  const qs = buildQueryString(params)
  const res = await apiService.get<ApiResponse<ProductReportResponse>>(`/reports/products${qs}`)
  return {
    data: res.data ?? { products: [], summary: { totalProducts: 0, totalShipments: 0 } },
    meta: res.meta ?? { page: 1, limit: 10, total: 0, totalPages: 0 },
  }
}

// --- Export ---
export async function exportReport(params: {
  type: string
  format?: 'xlsx' | 'csv'
  startDate?: string
  endDate?: string
  status?: string
}): Promise<Blob> {
  const qs = buildQueryString(params)
  const response = await apiService.instance.get(`/reports/export${qs}`, {
    responseType: 'blob',
  })
  return response.data
}

// --- Daily AI Report (one API call per module per day: analysis + recommendations + alerts) ---
export interface DailyModuleReportPayload {
  analysis: string
  recommendations: string[]
  alerts: string[]
}

export interface DailyAISummaryPayload {
  reportDate: string
  generatedAt: string
  modules: {
    contracts: DailyModuleReportPayload
    shipments: DailyModuleReportPayload
    clearing: DailyModuleReportPayload
    inventory: DailyModuleReportPayload
    finance: DailyModuleReportPayload
    reporting: DailyModuleReportPayload
  }
}

export interface DailyAISummaryReport {
  id: string
  reportDate: string
  payload: DailyAISummaryPayload
}

export async function getDailyAISummaryLatest(): Promise<DailyAISummaryReport | null> {
  const res = await apiService.get<ApiResponse<DailyAISummaryReport>>('/reports/daily-ai-summary/latest')
  if (!res?.success || !res?.data) return null
  return res.data
}

export async function runDailyAISummaryNow(): Promise<DailyAISummaryReport> {
  const res = await apiService.post<ApiResponse<DailyAISummaryReport>>('/reports/daily-ai-summary/run', {})
  if (!res?.success || !res?.data) throw new Error((res as { message?: string }).message || 'Failed to run daily AI summary')
  return res.data
}
